import { type Dashboard, type DashboardQuery, reportingKeys } from '@taler/api-client'
import { formatLocalDate, type LocalDate } from '@taler/domain'
import { useQuery } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'

export interface UseDashboardOptions {
  readonly ledgerId: () => string | undefined
  readonly displayCurrency: () => string
  readonly enabled: () => boolean
  readonly asOf: LocalDate
}

export interface UseDashboard {
  readonly data: ComputedRef<Dashboard | undefined>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  retry(): void
}

export function useDashboard(options: UseDashboardOptions): UseDashboard {
  const api = useApi()
  const asOf = formatLocalDate(options.asOf)
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)

  const query = computed<DashboardQuery>(() => ({
    asOf,
    displayCurrency: options.displayCurrency() as DashboardQuery['displayCurrency'],
  }))

  const dashboardQuery = useQuery({
    queryKey: computed(() => reportingKeys.dashboard(options.ledgerId() ?? '', query.value)),
    queryFn: ({ signal }) => api.reporting.dashboard(options.ledgerId()!, query.value, signal),
    enabled: active,
    retry: false,
  })

  return {
    data: computed(() => dashboardQuery.data.value),
    isLoading: computed(() => active.value && dashboardQuery.isPending.value),
    error: computed(() => dashboardQuery.error.value),
    retrying: retrying(dashboardQuery),
    retry: () => {
      void dashboardQuery.refetch()
    },
  }
}
